import { useEffect, useState } from "react";
import { History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { FOLLOWUP_STATUS_LABELS } from "@/lib/followupConstants";
import { FOLLOWUPS } from "@/constants/testIds";

const ACTION_LABELS = { contacted: "Contactada", postponed: "Pospuesta", discarded: "Descartada", reactivated: "Reactivada" };

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) + " \u00b7 " + d.toTimeString().slice(0, 5);
}

/** Historial de contactos/acciones de seguimiento de un paciente (transparencia
 * sobre quien contacto/poospuso/descarto y cuando). */
export default function FollowUpHistoryModal({ open, onOpenChange, patientId, patientName }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (!open || !patientId) return;
    setLoading(true);
    api.get(`/followups/patient/${patientId}/history`)
      .then(({ data }) => setContacts(data))
      .catch((err) => toast({ title: "Error al cargar el historial", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patientId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid={FOLLOWUPS.historyModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Historial de seguimiento</DialogTitle>
          <p className="text-sm text-muted-foreground">{patientName}</p>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground py-4">Cargando...</p>
        ) : contacts.length === 0 ? (
          <div className="text-center py-8" data-testid={FOLLOWUPS.historyEmptyState}>
            <History className="w-7 h-7 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Todavía no hay acciones registradas.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {contacts.map((c) => (
              <div key={c.id} className="border-b border-border pb-2.5 last:border-0" data-testid={`${FOLLOWUPS.historyItemPrefix}${c.id}`}>
                <p className="text-sm font-medium text-foreground">{ACTION_LABELS[c.action] || FOLLOWUP_STATUS_LABELS[c.action] || c.action}</p>
                <p className="text-xs text-muted-foreground">{formatDateTime(c.performed_at)} &middot; {c.performed_by_name}</p>
                {c.notes && <p className="text-xs text-muted-foreground mt-1">{c.notes}</p>}
                {c.new_due_date && <p className="text-xs text-muted-foreground mt-0.5">Nueva fecha: {new Date(c.new_due_date).toLocaleDateString("es-ES")}</p>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
